import { ref, computed, watch } from 'vue';
import { BehaviorSubject, throwError, EMPTY, switchMap, tap, scan, debounceTime, catchError } from 'rxjs';
import { useObservable } from './useObservable';
import { useDefaultGDKErrorHandler, onFetchLoaded } from './useGDKFetch';
import { scrollMore } from '@golden/shared';
export function defaultPaginationData(defaultData) {
    return {
        current_page: 0,
        data: defaultData,
        first_page_url: '',
        from: 0,
        last_page: 0,
        last_page_url: '',
        next_page_url: '',
        path: '',
        per_page: 25,
        prev_page_url: '',
        to: 0,
        total: 0
    };
}
export function usePagination(key, params, obs, errorHandler = useDefaultGDKErrorHandler()) {
    const page = ref(1);
    const isTriggerLoadMore = ref(false);
    const loading = ref(false);
    const isFirstLoad = computed(() => page.value === 1 && loading.value);
    const hasMore = computed(() => data.value.length && data.value.length !== lastRes.value?.total);
    const flow$ = new BehaviorSubject({ ...params.value, page: page.value });
    const resObs = flow$.pipe(debounceTime(page.value === 1 ? 0 : 250), tap(() => { loading.value = true; }), switchMap((params) => obs(params).pipe(catchError((error) => {
        page.value = lastRes.value?.current_page ?? 1;
        loading.value = false;
        errorHandler(error);
        throwError(() => error);
        return EMPTY;
    }))));
    const scanRes = resObs.pipe(scan((accu, lastRes) => {
        if (lastRes.current_page === undefined)
            return { data: [], lastRes };
        if (lastRes.current_page === 1)
            return { data: lastRes.data, lastRes };
        return { data: accu.data.concat(lastRes.data), lastRes };
    }, { data: [], lastRes: null }), tap(() => { loading.value = false; }));
    const res = useObservable(scanRes, { data: [], lastRes: null });
    const data = computed(() => res.value.data);
    const lastRes = computed(() => res.value.lastRes);
    const loadMore = () => {
        if (loading.value)
            return;
        isTriggerLoadMore.value = true;
        if (data.value.length >= lastRes.value.total)
            return;
        loading.value = true;
        page.value += 1;
        flow$.next({ ...params.value, page: page.value });
    };
    const finished = computed(() => {
        return isTriggerLoadMore.value && data.value.length >= lastRes.value.total;
    });
    const reload = (done) => {
        isTriggerLoadMore.value = false;
        loading.value = true;
        page.value = 1;
        flow$.next({ ...params.value, page: page.value });
        if (done) {
            onFetchLoaded(loading, done);
        }
    };
    const scroll = scrollMore(loadMore, 50);
    watch(() => params.value, () => { reload(); }, {
        deep: true
    });
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
        page,
        loading,
        isFirstLoad,
        hasMore,
        finished,
        [key]: data,
        lastRes,
        loadMore,
        reload,
        scroll
    };
}
//# sourceMappingURL=usePagination.js.map